/**
 * AiEditingConfig — the sky-replacement settings for an AI-editing line.
 *
 * Legacy has had this since AI editing shipped (`PurchaseFlow/common/
 * EditingProductListing/AIEditingPhotoSettings`), and the revamp dropped it: the
 * catalogue announces `requiredConfiguration: 'AI_EDITING'` on those products and
 * the transform mapped that to no configuration flow at all, so the drawer had
 * nothing to render. The v2 order has always accepted the setting back —
 * `AiEditingConfigurationDTO { details: { skyReplacement, cloudType } }`.
 *
 * It was collected in the product drawer until the source visuals moved into the
 * configuration step. These settings apply TO those visuals, so they
 * belong beside them: a client choosing a sky can see the photos it will be
 * applied to, and the drawer is left doing what it is for — picking and pricing.
 *
 * Same three cloud types, same thumbnails and the same `ai_settings` copy as
 * legacy — this is one offer with two front ends, so it must read identically in
 * both. The designer's prototype models it the same way (a toggle that reveals a
 * three-up image choice).
 */

import React from 'react'
import { Collapse, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BEIGE_600, CORAL_600, GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { ImageBoxButton } from 'components/common/ImageBoxButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUISwitch } from 'components/common/MUISwitch'
import { SkyReplacementOption } from 'models/purchaseFlow/AIEditingDTO'
import type { CartItemAiEditing } from 'models/purchaseFlowRevamp'
import skyClear from 'assets/img/ai_settings/clear.webp'
import skyCloudy from 'assets/img/ai_settings/cloudy.webp'
import skyLowCloud from 'assets/img/ai_settings/low_cloud.webp'

const THUMBNAIL_BY_OPTION: Readonly<Record<SkyReplacementOption, string>> = {
  [SkyReplacementOption.NO_CLOUD]: skyClear,
  [SkyReplacementOption.LOW_CLOUD]: skyLowCloud,
  [SkyReplacementOption.CLOUDY]: skyCloudy,
}

interface Props {
  value: CartItemAiEditing
  onToggleSkyReplacement: (enabled: boolean) => void
  onSelectCloudType: (cloudType: SkyReplacementOption) => void
}

/**
 * @example
 * <AiEditingConfig value={aiEditing} onToggleSkyReplacement={setSkyReplacement} onSelectCloudType={setCloudType} />
 */
export const AiEditingConfig: React.FC<Props> = ({ value, onToggleSkyReplacement, onSelectCloudType }) => {
  const { t } = useTranslation(['ai_settings'])

  return (
    <BorderBoxWrapper padding={2} elevation="none" borderColor={BEIGE_600}>

      <MUISwitch
        label={
          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {t('ai_settings:sky_replacement_title')}
          </Typography>
        }
        labelPlacement="end"
        checked={value.skyReplacement}
        onChange={() => onToggleSkyReplacement(!value.skyReplacement)}
      />

      <Collapse in={value.skyReplacement}>

        <MUIDivider margin={16} />

        {/* The three examples are a fixed 15.4rem each, so at 1.5 gap they need
            ~49rem — more than the product drawer's column has. They scroll
            INSIDE this box rather than shrinking: squeezing a sky thumbnail is
            what makes the three skies indistinguishable, which is the one thing
            this control exists to show. `flexShrink: 0` is what turns the
            overflow into a scroll instead of a squash. */}
        <Stack
          direction="row"
          gap={1.5}
          data-cy="pf-revamp-sky-replacement-options"
          sx={{
            overflowX: 'auto',
            // Room for the scrollbar so it never sits over the labels.
            paddingBottom: 0.4,
            '& > *': { flexShrink: 0 },
          }}
        >

          {Object.values(SkyReplacementOption).map((option) => (
            <ImageBoxButton
              key={option}
              altText={option}
              label={t(`ai_settings:sky_replacement_options.${option}`)}
              imageUrl={THUMBNAIL_BY_OPTION[option]}
              selected={value.cloudType === option}
              onClick={() => onSelectCloudType(option)}
            />
          ))}

        </Stack>

        {!value.cloudType &&
          <Typography variant="text-sm" color={CORAL_600} sx={{ mt: 1.2 }} role="alert">
            {t('ai_settings:sky_replacement_required')}
          </Typography>
        }

      </Collapse>

    </BorderBoxWrapper>
  )
}
